// Frontend mirror of the ``depth-findings/v1`` schema.
//
// Source: ``src/ai_alm/models/depth_findings.py``. Plan reference:
//   * plan/AI_ALM_Gate_G3_7_Implementation_Plan.md §6 (Phase 4 — PR-4)
//   * plan/AI_ALM_Gate_G3_7_Plan.md §4.3 (output schema)
//   * plan/AI_ALM_Gate_G3_7_Implementation_Plan.md §8 (Phase 6 frontend)
//
// The G3.7 panel and its sub-components consume these types either
// from the ``payload.finding`` cargo on each gate decision (the
// build_g37_content emit path) or from the on-disk envelopes
// served via ``/api/projects/{id}/files`` for inline previews.
//
// When the backend Pydantic models change, these types MUST be
// updated in lock-step. The G3.7 panel is the only consumer that
// reads the envelopes directly, so a drift here surfaces as a TS
// compile error first and (worst case) as a missing-field display
// at runtime — never as a data corruption.

/** Severity ladder used by depth findings + universal smells. */
export type Severity = 'critical' | 'high' | 'medium' | 'low'

/**
 * How the probe proposed to close the finding. Matches
 * ``ResolutionKind`` Literal in the backend Pydantic model.
 *
 * * ``edit_sr`` — replace the body of an existing SR with a new
 *   EARS clause. The clause has already passed the EARS-grammar
 *   guard (or been demoted to ``architect_only``).
 * * ``new_sr`` — append a brand-new SR row with the supplied EARS
 *   clause.
 * * ``architect_only`` — no patch; the architect must hand-write
 *   a clause via the Edit option. ``question`` carries the probe's
 *   prompt.
 * * ``defer_ok`` — the probe explicitly judges the gap acceptable
 *   to defer. The Approve option is still surfaced so the architect
 *   can record the deferral with no SRD edit.
 */
export type ResolutionKind = 'edit_sr' | 'new_sr' | 'architect_only' | 'defer_ok'

/** Pass-2 closure status (post-architect-patch re-probe outcome). */
export type ClosureStatus =
  | 'closed'
  | 'unchanged'
  | 'regressed'
  | 'not_evaluated'
  | 'deferred'
  | 'rejected_by_architect'

export interface SuggestedResolution {
  kind: ResolutionKind
  /** EARS clause body when ``kind`` is ``edit_sr`` / ``new_sr``. */
  patch?: string | null
  /** Probe's free-text question when ``kind`` is ``architect_only``. */
  question?: string | null
}

export interface DepthFinding {
  /** Stable id, e.g. F-001. Unique within the report. */
  id: string
  /** Rubric item this finding ties back to (e.g. 'frame_budget'). */
  rubric_item_id?: string | null
  /** Free-form domain tag rendered in the panel (e.g. 'iot/watchdog'). */
  category: string
  severity: Severity
  title: string
  /** One-sentence quote from the SRD that motivates the finding. */
  evidence: string
  /** Plain-English description of the expected requirement. */
  expected?: string | null
  /** SR-### ids the finding affects (may be empty). */
  linked_srs: string[]
  suggested_resolution: SuggestedResolution
}

export interface UniversalSmellOccurrence {
  id: string
  title: string
  /** Severity tag from the smells detector — string, not the Severity union. */
  severity: string
  count: number
  examples?: string[]
}

export interface RqsPreview {
  depth_score: number
  would_pass_g4: boolean
}

export interface TokensUsed {
  input: number
  output: number
  cache_read: number
  cache_creation: number
}

/**
 * Pass-1 ``depth-findings.json`` envelope. Carries everything the
 * G3.7 panel needs to render — findings, universal smells, RQS
 * preview, probe metadata. The panel reads ``input_hash`` to
 * decide whether to surface a "probe is stale" banner when the
 * SRD has been edited after the probe ran.
 */
export interface DepthFindings {
  schema_version: string
  shape: string
  rubric_id: string
  /** UTC ISO timestamp the probe completed. */
  ran_at: string
  /** LLM model id used (e.g. claude-sonnet-4-6). */
  model: string
  duration_ms: number
  tokens: TokensUsed
  cost_usd: number
  findings: DepthFinding[]
  universal_smells: UniversalSmellOccurrence[]
  rqs_preview: RqsPreview
  /** SHA-256 of canonicalised inputs the probe walked. */
  input_hash: string
  /** Non-null when the probe wrote a placeholder envelope (LLM error). */
  error?: string | null
  /**
   * Number of probe-proposed ``edit_sr`` patches the EARS-grammar
   * guard demoted to ``architect_only``. Surfaced inline in the
   * G3.7 summary banner so the architect knows some patches will
   * require manual EARS clauses.
   */
  patches_demoted_by_ears_guard: number
}

export interface Pass2Finding {
  id: string
  severity: Severity
  closure_status: ClosureStatus
  title: string
  /** Optional architect-facing note from the closure check. */
  note?: string | null
}

/**
 * Pass-2 ``depth-findings-pass2.json`` envelope. Carries the
 * post-architect-patch closure status for every pass-1 finding so
 * the G3.7 panel can render closure badges and the G4 banner can
 * surface regressions.
 */
export interface DepthFindingsPass2 {
  schema_version: string
  shape: string
  rubric_id: string
  ran_at: string
  model: string
  duration_ms: number
  tokens: TokensUsed
  cost_usd: number
  findings: Pass2Finding[]
  error?: string | null
  input_hash: string
}

// --------------------------------------------------------------------------- //
// Decision payload — shape of the inline ``finding`` cargo the
// build_g37_content emit attaches to each ``GateDecision.payload``.
// The ContentAwareG37Gate panel reads from this without round-
// tripping through the on-disk envelope.
// --------------------------------------------------------------------------- //

/** Stable G3.7 option ids — mirror ``gate_emitter.G37_OPTION_*``. */
export const G37_OPTION_APPROVE = 'approve'
export const G37_OPTION_EDIT = 'edit'
export const G37_OPTION_DEFER = 'defer'
export const G37_OPTION_REJECT = 'reject'
/**
 * Promote a G3.7 ``architect_only`` finding into a tracked
 * Deferred Unknown so it lands in §3a of the exported requirements
 * pack and is consumed by downstream agents on subsequent runs.
 * (Architect Decisions Downstream Flow Plan — G3.7 → DU promotion
 * follow-up.) Only offered on findings whose probe-proposed
 * resolution kind is ``architect_only``.
 */
export const G37_OPTION_PROMOTE = 'promote_to_du'

/** All option ids as a union for exhaustive switches. */
export type G37OptionId =
  | typeof G37_OPTION_APPROVE
  | typeof G37_OPTION_EDIT
  | typeof G37_OPTION_DEFER
  | typeof G37_OPTION_REJECT
  | typeof G37_OPTION_PROMOTE

/**
 * One option card emitted by ``build_g37_content`` per finding. The
 * ContentAwareG37Gate panel renders these as clickable cards with
 * an inline editor for ``needs_text === true``.
 */
export interface G37Option {
  id: G37OptionId | string
  label: string
  summary: string
  destructive?: boolean
  needs_text?: boolean
  /** Status the resolver maps the option to on the wire. */
  maps_to_status?: 'approved' | 'edited' | 'skipped' | 'rejected'
}

/**
 * The inline payload ``build_g37_content`` attaches to every G3.7
 * gate decision. The ``finding`` cargo carries the full
 * ``DepthFinding`` so the panel can render evidence + suggested
 * resolution without re-fetching the envelope.
 */
export interface G37DecisionPayload {
  kind: 'depth_finding'
  finding_id: string
  severity: Severity
  category?: string | null
  title: string
  evidence?: string | null
  expected?: string | null
  linked_srs: string[]
  rubric_id?: string | null
  shape?: string | null
  resolution_kind: ResolutionKind
  suggested_patch?: string | null
  probe_question?: string | null
  options: G37Option[]
  /** Full finding payload — convenience copy for the panel. */
  finding: DepthFinding
}
